import type { Account, Backtest, OrderRecord } from "@/api/types";
import { isLiveInstance } from "@/lib/live";
import { providerLabel } from "@/lib/account";
import { formatDateTimeShort } from "@/lib/dates";
import type { TimeZoneMode } from "@/stores/timezone";

export type SearchGroup = "Strategies" | "Symbols" | "Accounts" | "Orders" | "Go to";

export type SearchItem = {
  id: string;
  group: SearchGroup;
  label: string;
  hint?: string;
  to: string;
  /** Extra text matched but not shown (e.g. symbol, provider). */
  keywords?: string;
};

export const GROUP_ORDER: SearchGroup[] = ["Strategies", "Symbols", "Accounts", "Orders", "Go to"];

const PAGES: SearchItem[] = [
  { id: "p-new-bt", group: "Go to", label: "New backtest", to: "/lab/backtest/new", keywords: "create run test" },
  { id: "p-go-live", group: "Go to", label: "Go live", to: "/live/new", keywords: "new strategy deploy" },
  { id: "p-connect", group: "Go to", label: "New account", to: "/integrations/marketplaces/new", keywords: "add connect exchange marketplace api key" },
  { id: "p-orders", group: "Go to", label: "Trades", to: "/trades", keywords: "orders ledger fills" },
  { id: "p-profile", group: "Go to", label: "Profile", to: "/account/profile", keywords: "account name" },
  { id: "p-security", group: "Go to", label: "Security", to: "/account/security", keywords: "password" },
  { id: "p-prefs", group: "Go to", label: "Preferences", to: "/account/preferences", keywords: "theme time zone utc local chart" },
];

const PER_GROUP = 5;

/** Build the searchable items from what the app already has in memory. */
export function buildSearchItems(data: {
  backtests: Backtest[];
  accounts: Account[];
  orders: OrderRecord[];
  tzMode?: TimeZoneMode;
}): SearchItem[] {
  const items: SearchItem[] = [];
  const accountName = (id: string) => data.accounts.find((a) => a.id === id)?.name || "";

  for (const bt of data.backtests) {
    const live = isLiveInstance(bt);
    items.push({
      id: `bt-${bt.id}`,
      group: "Strategies",
      label: bt.name || "Untitled",
      hint: [live ? "Live" : "Backtest", bt.symbol, bt.timeframe].filter(Boolean).join(" · "),
      to: live ? `/live/${bt.id}` : `/lab/backtest/${bt.id}`,
      keywords: `${bt.symbol} ${live ? "live" : "backtest"}`,
    });
  }

  const symbols = new Map<string, { count: number; allLive: boolean }>();
  for (const bt of data.backtests) {
    if (!bt.symbol) continue;
    const cur = symbols.get(bt.symbol) ?? { count: 0, allLive: true };
    symbols.set(bt.symbol, { count: cur.count + 1, allLive: cur.allLive && isLiveInstance(bt) });
  }
  for (const [symbol, { count, allLive }] of symbols) {
    items.push({
      id: `sym-${symbol}`,
      group: "Symbols",
      label: symbol,
      hint: `${count} ${count === 1 ? "strategy" : "strategies"}`,
      to: `${allLive ? "/live" : "/lab/backtest"}?q=${encodeURIComponent(symbol)}`,
    });
  }

  for (const acc of data.accounts) {
    items.push({
      id: `acc-${acc.id}`,
      group: "Accounts",
      label: acc.name || providerLabel(acc.provider),
      hint: providerLabel(acc.provider),
      to: `/integrations/marketplaces/${acc.id}`,
      keywords: acc.provider,
    });
  }

  for (const o of data.orders) {
    // created_at may arrive as a string, epoch number or Date depending on the transport.
    const date = o.created_at ? formatDateTimeShort(o.created_at as string | number | Date, data.tzMode) : "";
    items.push({
      id: `ord-${o.id}`,
      group: "Orders",
      label: `${o.side} ${o.symbol}`.trim(),
      hint: [o.status, date, accountName(o.account_id)].filter(Boolean).join(" · "),
      to: "/trades",
      keywords: `${o.exchange_order_id} ${o.id} ${o.position_side}`,
    });
  }

  return items.concat(PAGES);
}

function score(item: SearchItem, needle: string): number {
  const label = item.label.toLowerCase();
  if (label === needle) return 0;
  if (label.startsWith(needle)) return 1;
  if (label.split(/[\s/._-]+/).some((w) => w.startsWith(needle))) return 2;
  if (label.includes(needle)) return 3;
  const rest = `${item.hint ?? ""} ${item.keywords ?? ""}`.toLowerCase();
  if (rest.includes(needle)) return 4;
  return -1;
}

/**
 * Filter and rank items. Every word of the query must match; results are
 * grouped in GROUP_ORDER with at most PER_GROUP items each.
 * An empty query returns the given recent strategies plus the Go to pages.
 */
export function searchItems(items: SearchItem[], query: string, recentIds: string[] = []): SearchItem[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) {
    const recent = recentIds
      .map((id) => items.find((i) => i.id === id))
      .filter((i): i is SearchItem => !!i);
    return [...recent, ...items.filter((i) => i.group === "Go to")];
  }

  const ranked = items
    .map((item) => {
      const scores = words.map((w) => score(item, w));
      return { item, s: scores.some((s) => s < 0) ? -1 : Math.max(...scores) };
    })
    .filter((r) => r.s >= 0);

  const out: SearchItem[] = [];
  for (const group of GROUP_ORDER) {
    out.push(
      ...ranked
        .filter((r) => r.item.group === group)
        .sort((a, b) => a.s - b.s || a.item.label.localeCompare(b.item.label))
        .slice(0, PER_GROUP)
        .map((r) => r.item)
    );
  }
  return out;
}
